/**
 * WhatsApp Link Component
 *
 * A compact icon button that opens a WhatsApp chat for a phone number in a
 * new tab. It renders nothing when no safe WhatsApp URL can be resolved
 * (masked numbers, local numbers without a country code).
 */

import IconButton from '@mui/material/IconButton';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { alpha, useTheme } from '@mui/material/styles';

import { isMaskedValue } from '@/shared/utils/isMaskedValue';

import { resolveWhatsAppUrl } from './resolveWhatsAppUrl';

export interface WhatsAppLinkProps {
  /** Phone number as displayed to the operator. */
  number: string;
  /** Optional backend-built WhatsApp URL; wins over the number fallback. */
  backendUrl?: string | null;
}

/**
 * WhatsApp action for a phone number. Renders nothing when the number cannot
 * be resolved into a safe WhatsApp URL.
 */
export function WhatsAppLink({ number, backendUrl }: WhatsAppLinkProps) {
  const theme = useTheme();
  if (isMaskedValue(number)) {
    return null;
  }

  const href = resolveWhatsAppUrl(number, backendUrl);

  if (!href) {
    return null;
  }

  return (
    <IconButton
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`WhatsApp ${number}`}
      size="small"
      onClick={event => event.stopPropagation()}
      sx={{
        flexShrink: 0,
        color: 'primary.main',
        width: 24,
        height: 24,
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
        '&:focus-visible': {
          outline: '2px solid #3b82f6',
          outlineOffset: 2,
          boxShadow: '0 0 0 3px rgba(59,130,246,0.15)',
        },
      }}
    >
      <WhatsAppIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
}
